import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { dateTimeText, RefusalQuietResponse, RefusalRetryResponse, RefusalRow, RefusalsResponse } from './api'
import { StatusPill } from './DashboardPage'
import styles from './GodMode.module.css'
import dash from './DashboardPage.module.css'

// The Crew records Control refused at one client this week (API
// services/god-dashboard.js readRefusals), one row per record. A phone keeps a
// refused record and sends it again at every sync, so Tries is how often it
// came; once Control takes it (a resend after a fix) it reads Taken and no
// longer counts on the dashboard. A worker tag names its picker, so a refused
// handover shows whose bucket it was. Control also keeps the whole record
// (kept), so Retry sends it through again without the phone, should the phone
// be lost or flat. Refuse quietly lets go of one that can never be taken.

const KIND: Record<string, string> = {
  receipt: 'Receipt',
  pick: 'Pick',
  quality_issue: 'QC',
  clock_on: 'Clock on',
  clock_off: 'Clock off',
}

function scanned(r: RefusalRow) {
  if (r.tag_name || r.tag_person) {
    return (
      <>
        Tag of {r.tag_person || r.tag_name}
        {r.tag_person && r.tag_name && r.tag_name !== r.tag_person && <div className={dash.sub}>tag says {r.tag_name}</div>}
        {!r.tag_person && <div className={dash.sub}>not a known person here</div>}
      </>
    )
  }
  return r.crate_id ? <code>{r.crate_id}</code> : <span className={dash.none}>Not kept</span>
}

export function RefusalsPage() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const [data, setData] = useState<RefusalsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await apiJson<RefusalsResponse>(user, `/api/god-mode/clients/${id}/refusals`))
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [user, id])

  useEffect(() => {
    load()
  }, [load])

  const retry = async (recordIds?: string[]) => {
    setBusy(true)
    setResult(null)
    try {
      const out = await apiJson<RefusalRetryResponse>(user, `/api/god-mode/clients/${id}/refusals/retry`, {
        method: 'POST',
        body: recordIds ? { record_ids: recordIds } : {},
      })
      const why = [...new Set(Object.values(out.reasons))]
      setResult(
        out.tried === 0
          ? 'Nothing to retry.'
          : `${out.taken} taken, ${out.refused} still refused${why.length ? ` (${why.join(', ')})` : ''}.`
      )
      await load()
    } catch (e) {
      setResult((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const quiet = async (r: RefusalRow) => {
    if (!window.confirm(`Refuse ${r.record_id} quietly? It stops counting as refused, and the phone is told it was taken and lets it go. Control never takes it.`)) return
    setBusy(true)
    setResult(null)
    try {
      const out = await apiJson<RefusalQuietResponse>(user, `/api/god-mode/clients/${id}/refusals/quiet`, {
        method: 'POST',
        body: { record_ids: [r.record_id] },
      })
      setResult(out.quieted ? 'Refused quietly: the phone lets it go at its next sync.' : 'Nothing refused quietly: it was taken or already quiet.')
      await load()
    } catch (e) {
      setResult((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const open = data?.refusals.filter((r) => !r.taken && !r.quiet) || []
  const retryable = open.filter((r) => r.kept)
  const reasons = Object.entries(
    open.reduce<Record<string, number>>((m, r) => ({ ...m, [r.reason]: (m[r.reason] || 0) + 1 }), {})
  ).sort((a, b) => b[1] - a[1])

  return (
    <>
      <PageHeader title={data ? `Refused Crew records · ${data.client.name}` : 'Refused Crew records'} />
      <PageBody>
        <div className={styles.stack}>
          <div>
            <Link to="/god-mode" className={styles.secondary}>
              ← Back to dashboard
            </Link>{' '}
            <button type="button" className={styles.secondary} onClick={load}>
              Refresh
            </button>{' '}
            {retryable.length > 0 && (
              <button type="button" className={styles.secondary} disabled={busy} onClick={() => retry()}>
                Retry all ({retryable.length})
              </button>
            )}
          </div>
          {result && <p className={styles.lead}>{result}</p>}
          {error && <p className={styles.error}>{error}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}
          {data && (
            <>
              <p className={styles.lead}>
                {open.length
                  ? `${open.length} record${open.length === 1 ? '' : 's'} still refused in the last ${data.days} days`
                  : `Nothing still refused in the last ${data.days} days`}
                {reasons.length > 0 && `: ${reasons.map(([why, n]) => `${n} ${why}`).join(', ')}`}.
              </p>
              <div className={styles.tableCard}>
                {data.refusals.length === 0 ? (
                  <div className={styles.empty}>No refusals.</div>
                ) : (
                  <table className={styles.table} aria-label="Refused Crew records">
                    <thead>
                      <tr>
                        <th>Last sent</th>
                        <th>Record</th>
                        <th>Scanned</th>
                        <th>Sent by</th>
                        <th>Why</th>
                        <th title="How often the phone sent it">Tries</th>
                        <th>Now</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.refusals.map((r) => (
                        <tr key={r.record_id}>
                          <td>
                            {dateTimeText(r.last_at)}
                            {r.first_at !== r.last_at && <div className={dash.sub}>first {dateTimeText(r.first_at)}</div>}
                          </td>
                          <td>
                            {(r.event_type && KIND[r.event_type]) || r.event_type || '—'}
                            <div className={dash.sub}>{r.record_id}</div>
                          </td>
                          <td>{scanned(r)}</td>
                          <td>{r.sent_by || <span className={dash.none}>Unknown</span>}</td>
                          <td>{r.reason}</td>
                          <td>{r.tries}</td>
                          <td>
                            {r.taken ? (
                              <StatusPill status="ok">Taken</StatusPill>
                            ) : r.quiet ? (
                              <StatusPill status="idle">Refused quietly</StatusPill>
                            ) : (
                              <StatusPill status="warn">Refused</StatusPill>
                            )}
                            {!r.taken && !r.quiet && (
                              <div>
                                {r.kept && (
                                  <button
                                    type="button"
                                    className={styles.secondary}
                                    disabled={busy}
                                    aria-label={`Retry ${r.record_id}`}
                                    onClick={() => retry([r.record_id])}
                                  >
                                    Retry
                                  </button>
                                )}{' '}
                                <button
                                  type="button"
                                  className={styles.secondary}
                                  disabled={busy}
                                  aria-label={`Refuse ${r.record_id} quietly`}
                                  onClick={() => quiet(r)}
                                >
                                  Refuse quietly
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <p className={dash.sub}>
                A refused record stays on the phone, which sends it again at every sync. "Worker tag names no person": the picker clocked on with
                no signal, so their tag names their sign-in, not their person; it is taken once that sign-in has clocked on here with signal, and
                Control then sends its own copy of the record again too. Retry sends that copy now, without the phone; records refused before
                Control kept copies can only come from the phone. Refuse quietly lets go of a record that can never be taken, such as a clock-off
                for a shift clocked on somewhere else: it no longer counts as refused, and the phone is told it was taken and drops it.
              </p>
            </>
          )}
        </div>
      </PageBody>
    </>
  )
}
